/**
 * "Install the app" card for people on the website. The site is a PWA, so no app store is needed:
 * Android/desktop Chrome offer a real install prompt, iPhone/iPad (and browsers without a prompt) get step-by-step instructions.
 */
import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { isNative } from '../native/platform';
import { Modal } from './ui';

interface InstallEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const HIDE_KEY = 'cardhub.installHiddenUntil';
const HIDE_DAYS = 14;

// The browser fires this once, early, so keep it at module level instead of waiting for a component to mount.
let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isMobile = () => /Android|iPad|iPhone|iPod/i.test(navigator.userAgent) || isIos();

function hiddenNow(): boolean {
  try {
    return Number(localStorage.getItem(HIDE_KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}

export function InstallApp() {
  const t = useT();
  const [, bump] = useState(0);
  const [hidden, setHidden] = useState(hiddenNow);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const l = () => bump((n) => n + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);

  if (isNative || isStandalone() || hidden) return null;
  const ios = isIos();
  // Without a native prompt, only phones/tablets get the how-to card (desktop browsers have their own install button).
  if (!deferred && !isMobile()) return null;

  const hide = () => {
    try {
      localStorage.setItem(HIDE_KEY, String(Date.now() + HIDE_DAYS * 86_400_000));
    } catch {
      /* private mode: it will simply show again next visit */
    }
    setHidden(true);
  };

  const install = async () => {
    if (!deferred) {
      setHelp(true);
      return;
    }
    const ev = deferred;
    deferred = null;
    await ev.prompt();
    const choice = await ev.userChoice.catch(() => null);
    if (choice?.outcome === 'dismissed') hide();
    else bump((n) => n + 1);
  };

  return (
    <>
      <div className="card install-card" role="region" aria-label={t('install.title')}>
        <img src="/icon-192.png" alt="" width={44} height={44} className="install-icon" />
        <div className="grow">
          <strong>{t('install.title')}</strong>
          <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
            {t('install.body')}
          </div>
        </div>
        <div className="col" style={{ gap: 6 }}>
          <button className="btn sm primary" onClick={() => void install()}>
            {t('install.button')}
          </button>
          <button className="btn sm ghost" onClick={hide}>
            {t('install.later')}
          </button>
        </div>
      </div>
      <Modal open={help} onClose={() => setHelp(false)} title={t('install.howTo')}>
        <p className="dim">{ios ? t('install.ios') : t('install.android')}</p>
        <button className="btn primary block" style={{ marginTop: 12 }} onClick={() => setHelp(false)}>
          OK
        </button>
      </Modal>
    </>
  );
}
